// Global Instructions Rule Applied!
//
// Configuration → DocuSign Envelopes (admin only).
//
// The manual-link surface for the DocuSign pipeline (deal follower split,
// owner 2026-08-25): envelopes arriving at monitored mailboxes from
// docusign.net are auto-matched to an open execution deal only when the
// subject names exactly one candidate — everything ambiguous lands here as
// 'unmatched' for a person to link (which mints the timeline interaction,
// wakes the deal_execution_follower and fires the docusign_received signal)
// or dismiss (not deal-related). Matched/linked rows are shown for audit;
// linked envelopes cannot be unlinked (the interaction and signal exist).
//
// Chrome copied from InteractionTrackingPanel: FormSection + AntD Table,
// standard Tag vocabulary. Toasts via react-toastify (AntD static message
// renders nowhere on React 19).
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Popconfirm, Segmented, Select, Table, Tag, Tooltip } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  dismissDocusignEnvelope,
  fetchDocusignEnvelopes,
  linkDocusignEnvelope
} from '../../internal_agents/services/internalAgentsService.ts'
import { DocusignEnvelope } from '../../../state_manager/data_models/internal_agents_model.ts'
import { searchDealsForPopup } from '../../deals/services/dealsService.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'

const STATUS_TAG: Record<string, string> = {
  matched: 'green',
  manually_linked: 'green',
  unmatched: 'gold',
  dismissed: 'default'
}

const STATUS_LABEL: Record<string, string> = {
  matched: 'Matched',
  manually_linked: 'Linked',
  unmatched: 'Unmatched',
  dismissed: 'Dismissed'
}

export default function DocusignEnvelopesPanel(): React.ReactElement {
  const [rows, setRows] = useState<DocusignEnvelope[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>('unmatched')
  const [linking, setLinking] = useState<DocusignEnvelope | null>(null)
  const [dealOptions, setDealOptions] = useState<Array<{ value: string; label: string }>>([])
  const [dealSearching, setDealSearching] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // Call-time thunk, not a render-time snapshot — see FollowerAgentsPanel:
  // a token captured at mount outlives the JWT and surfaces as 403s once
  // the panel has been open for over an hour.
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchDocusignEnvelopes(filter === 'all' ? null : filter, token()))
    } catch (error) {
      console.error('[configuration] docusign envelopes load failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to load envelopes')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [filter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load()
  }, [load])

  const handleDealSearch = useCallback(
    async (term: string) => {
      if (!term || term.trim().length < 2) {
        setDealOptions([])
        return
      }
      setDealSearching(true)
      try {
        const result = await searchDealsForPopup(term, 10, token())
        setDealOptions(
          (result.data ?? []).map((deal: any) => ({
            value: String(deal[DealFields.deals__id]),
            label: String(deal[DealFields.deals__name] ?? deal[DealFields.deals__id])
          }))
        )
      } catch {
        setDealOptions([])
      } finally {
        setDealSearching(false)
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleLinkConfirm = async () => {
    if (!linking || !selectedDeal) return
    setSaving(true)
    try {
      await linkDocusignEnvelope(linking.docusign_envelopes__id, selectedDeal, token())
      toast.success('Envelope linked — the execution follower has been alerted')
      setLinking(null)
      setSelectedDeal(null)
      setDealOptions([])
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to link envelope')
    } finally {
      setSaving(false)
    }
  }

  const handleDismiss = async (envelope: DocusignEnvelope) => {
    try {
      await dismissDocusignEnvelope(envelope.docusign_envelopes__id, token())
      toast.success('Envelope dismissed')
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to dismiss envelope')
    }
  }

  const columns = useMemo(
    () => [
      {
        title: 'Received',
        dataIndex: 'docusign_envelopes__received_at',
        width: 110,
        render: (value: string | null, record: DocusignEnvelope) =>
          dayjs(value ?? record.docusign_envelopes__date_created).format('MM/DD/YYYY')
      },
      {
        title: 'Subject',
        dataIndex: 'docusign_envelopes__subject',
        ellipsis: true,
        render: (value: string | null) => value || <span className='text-gray-400'>—</span>
      },
      {
        title: 'Mailbox',
        dataIndex: 'docusign_envelopes__mailbox_email',
        width: 200,
        ellipsis: true
      },
      {
        title: 'Status',
        dataIndex: 'docusign_envelopes__status',
        width: 110,
        render: (value: string, record: DocusignEnvelope) => (
          <Tooltip
            title={
              record.docusign_envelopes__match_method
                ? `via ${record.docusign_envelopes__match_method.replace('_', ' ')}`
                : undefined
            }
          >
            <Tag color={STATUS_TAG[value] ?? 'default'}>{STATUS_LABEL[value] ?? value}</Tag>
          </Tooltip>
        )
      },
      {
        title: 'Deal',
        dataIndex: 'deals__name',
        ellipsis: true,
        render: (value: string | null) => value || <span className='text-gray-400'>—</span>
      },
      {
        title: '',
        key: 'actions',
        width: 170,
        render: (_: unknown, record: DocusignEnvelope) =>
          record.docusign_envelopes__status === 'unmatched' ? (
            <div className='flex gap-1.5'>
              <Button size='small' onClick={() => setLinking(record)}>
                Link to deal…
              </Button>
              <Popconfirm
                title='Dismiss this envelope?'
                description='Marks it as not deal-related. It stays here for audit.'
                onConfirm={() => handleDismiss(record)}
              >
                <Button size='small' danger ghost>
                  Dismiss
                </Button>
              </Popconfirm>
            </div>
          ) : null
      }
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <FormSection
      data-component='DocusignEnvelopesPanel'
      title='DocuSign Envelopes'
      titleColor='blue'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={
        <div className='flex items-center gap-2'>
          <Segmented
            size='small'
            value={filter}
            onChange={(value) => setFilter(String(value))}
            options={[
              { label: 'Unmatched', value: 'unmatched' },
              { label: 'Matched', value: 'matched' },
              { label: 'Linked', value: 'manually_linked' },
              { label: 'Dismissed', value: 'dismissed' },
              { label: 'All', value: 'all' }
            ]}
          />
          <Button size='small' icon={<ReloadOutlined />} onClick={load} />
        </div>
      }
    >
      <p className='text-gray-500 text-xs mt-0 mb-2'>
        DocuSign envelopes detected at the monitored mailboxes. Auto-matching links an envelope
        only when its subject names exactly one open execution deal — everything else waits here.
        Linking creates the timeline interaction, wakes the execution follower and fires its
        DocuSign signal; linked envelopes cannot be unlinked.
      </p>
      <Table
        size='small'
        rowKey='docusign_envelopes__id'
        columns={columns as any}
        dataSource={rows}
        pagination={rows.length > 25 ? { pageSize: 25 } : false}
        locale={{ emptyText: loading ? ' ' : 'No envelopes' }}
      />

      <Modal
        title='Link envelope to a deal'
        open={linking != null}
        onOk={handleLinkConfirm}
        okText='Link envelope'
        okButtonProps={{ disabled: !selectedDeal }}
        confirmLoading={saving}
        onCancel={() => {
          setLinking(null)
          setSelectedDeal(null)
          setDealOptions([])
        }}
        destroyOnClose
      >
        <p className='text-gray-600'>
          “{linking?.docusign_envelopes__subject || 'DocuSign envelope'}” — linking creates an
          interaction on the deal's timeline, wakes its execution follower and fires the DocuSign
          signal on the Execution tab.
        </p>
        <Select
          showSearch
          className='w-full'
          placeholder='Search deals by name…'
          filterOption={false}
          onSearch={handleDealSearch}
          loading={dealSearching}
          options={dealOptions}
          value={selectedDeal}
          onChange={(value) => setSelectedDeal(value)}
          notFoundContent={dealSearching ? 'Searching…' : 'Type at least 2 characters'}
        />
      </Modal>
    </FormSection>
  )
}
